import { useCallback } from 'react'
import type { JSX } from 'react'
import type * as React from 'react'

import { Dialog, Popover } from '@buffer-mono/popcorn'

import { useCalendarMobileMode } from '~publish/components/Calendar/hooks/useCalendarMobileMode'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { CalendarPostCard } from '../CalendarPostCard'
import { CalendarPostCard_Post } from '../fragments/CalendarPostCard_Post'
import { useLoadCalendarPostCard } from '../hooks/useLoadCalendarPostCard'

import styles from './CalendarPostPopover.module.css'

type CalendarPostPopoverProps = {
  children: React.ReactNode
  post: FragmentOf<typeof CalendarPostCard_Post>
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  side?: 'left' | 'right'
}

export const CalendarPostPopover = ({
  children,
  post: postFragment,
  isOpen,
  onOpenChange,
  side = 'right',
}: CalendarPostPopoverProps): JSX.Element => {
  const calendarPost = readFragment(CalendarPostCard_Post, postFragment)
  const { data } = useLoadCalendarPostCard(calendarPost.id, {
    skip: !isOpen,
  })
  const isMobile = useCalendarMobileMode()

  const closeWhenComposerOpensOrLinkClicks = (
    evt: React.MouseEvent<HTMLElement>,
  ): void => {
    const target = evt.target
    if (!(target instanceof Element)) return

    if (target.closest('a')) {
      onOpenChange(false)
      return
    }

    const openComposerTrigger = target.closest(
      '[aria-controls="composer-root"]',
    )
    if (openComposerTrigger) {
      onOpenChange(false)
    }
  }

  /**
   * Handle clicks on the dialog backdrop (outside content).
   * We prevent default to stop Radix from closing the dialog immediately,
   * then close it after a microtask. This ensures the click event is fully
   * processed by the overlay before it disappears, preventing the click
   * from propagating to elements underneath (like the Day cell).
   */
  const handlePointerDownOutside = useCallback(
    (e: CustomEvent<{ originalEvent: PointerEvent }>): void => {
      e.preventDefault()
      e.stopPropagation()
      queueMicrotask(() => {
        onOpenChange(false)
      })
    },
    [onOpenChange],
  )

  if (isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <Dialog.Trigger>{children}</Dialog.Trigger>
        <Dialog.Content
          className={styles.dialogContent}
          onClick={closeWhenComposerOpensOrLinkClicks}
          onPointerDownOutside={handlePointerDownOutside}
        >
          <Dialog.CloseButton />
          <CalendarPostCard post={data?.post} partialData={postFragment} />
        </Dialog.Content>
      </Dialog>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content
        side={side}
        align="start"
        className={styles.popoverContent}
        onClick={closeWhenComposerOpensOrLinkClicks}
      >
        <CalendarPostCard post={data?.post} partialData={postFragment} />
      </Popover.Content>
    </Popover>
  )
}
